import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import DashboardEstimation from "@/pages/DashboardEstimation";
import Projects from "@/pages/Projects";
import ProjectPlanning from "@/pages/ProjectPlanning";
import ProjectPlanningList from "@/pages/ProjectPlanningList";
import ResourceAllocation from "@/pages/ResourceAllocation";
import ProjectDetail from "@/pages/ProjectDetail";
// Kanban removed - replaced by Project Planning module
// import Kanban from "@/pages/Kanban";
import Timesheets from "@/pages/Timesheets";
import TimesheetCalendar from "@/pages/TimesheetCalendar";
import TimesheetReports from "@/pages/TimesheetReports";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Clients from "@/pages/Clients";
import Leads from "@/pages/Leads";
import LeadApprovals from "@/pages/LeadApprovals";
import Interns from "@/pages/Interns";
import Estimations from "@/pages/Estimations";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import CompanySettings from "@/pages/CompanySettings";
import Employees from "@/pages/Employees";
import Vendors from "@/pages/Vendors";
import Teams from "@/pages/Teams";
import Sales from "@/pages/Sales";
import RFQs from "@/pages/RFQs";
import PermissionMatrix from "@/pages/PermissionMatrix";
import RBACViewer from "@/pages/RBACViewer";
import ErrorSettings from "@/pages/ErrorSettings";
import ErrorTracking from "@/pages/ErrorTracking";
import WebhookSettings from "@/pages/WebhookSettings";
import RFILog from "@/pages/RFILog";
import TransmittalLog from "@/pages/TransmittalLog";
import ChangeOrderLog from "@/pages/ChangeOrderLog";
import NoAccess from "@/pages/NoAccess";
import EmergencyLogout from "@/pages/EmergencyLogout";
import LeaveManagement from "@/pages/LeaveManagement";
import LeaveApprovals from "@/pages/LeaveApprovals";
import HRLeaveAllocations from "@/pages/HRLeaveAllocations";
import LeavePolicyConfig from "@/pages/LeavePolicyConfig";
import LeaveReports from "@/pages/LeaveReports";
import MyWorkspace from "@/pages/MyWorkspace";
import CheckingQueue from "@/pages/CheckingQueue";
import QualityDashboard from "@/pages/QualityDashboard";
import PlanningBoard from "@/pages/PlanningBoard";
import HolidayCalendar from "@/pages/HolidayCalendar";
import WBSMasterList from "@/pages/WBSMasterList";
import WBSEstimations from "@/pages/WBSEstimations";
// Performance Management Module
import KRASettings from "@/pages/KRASettings";
import EmployeeTargets from "@/pages/EmployeeTargets";
import PerformanceSummary from "@/pages/PerformanceSummary";
import PerformanceAwards from "@/pages/PerformanceAwards";
import MyPerformance from "@/pages/MyPerformance";
import PerformanceReports from "@/pages/PerformanceReports";
import ClientFeedback from "@/pages/ClientFeedback";
import ClientFeedbackReport from "@/pages/ClientFeedbackReport";
// Expense Tracking Module
import ExpenseSettings from "@/pages/ExpenseSettings";
import ExpenseEntry from "@/pages/ExpenseEntry";
import ExpenseEmployeeCost from "@/pages/ExpenseEmployeeCost";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle blob error responses (e.g., from file downloads)
    if (error.response?.data instanceof Blob && error.response?.data.type === 'application/json') {
      try {
        const text = await error.response.data.text();
        const jsonError = JSON.parse(text);
        error.response.data = jsonError;
      } catch (e) {
        // If parsing fails, keep the original blob
        console.error('Failed to parse blob error response:', e);
      }
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

function PrivateRoute({ children }) {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/login" />;
}

// Role-based route protection for restricted users
function RestrictedRoute({ children, allowedFor = [] }) {
  const token = localStorage.getItem("token");
  const userStr = localStorage.getItem("user");
  
  if (!token) {
    return <Navigate to="/login" />;
  }
  
  if (!userStr) {
    return <Navigate to="/login" />;
  }
  
  const user = JSON.parse(userStr);
  
  // RBAC handles all access control through permissions
  // No hardcoded restrictions needed - Layout will show/hide pages based on user.permissions
  return children;
}

function App() {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  // Refresh the cached user from the server so department / access-level changes apply without re-login
  useEffect(() => {
    if (!localStorage.getItem("token")) return;
    api.get("/auth/me").then((res) => {
      setUser((prev) => {
        const fresh = { ...(prev || {}), ...res.data, permissions: res.data?.permissions || prev?.permissions };
        localStorage.setItem("user", JSON.stringify(fresh));
        return fresh;
      });
    }).catch(() => {});
  }, []);

  // Prevent number input from changing on mouse scroll
  useEffect(() => {
    const preventNumberScroll = (e) => {
      // Check if the active element is a number input
      if (document.activeElement.type === 'number') {
        e.preventDefault();
      }
    };

    // Add event listener to prevent scroll on number inputs
    document.addEventListener('wheel', preventNumberScroll, { passive: false });

    return () => {
      document.removeEventListener('wheel', preventNumberScroll);
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route path="/no-access" element={<NoAccess user={user} />} />
          <Route path="/logout" element={<EmergencyLogout />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Dashboard user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/my-workspace"
            element={
              <PrivateRoute>
                <MyWorkspace user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/checking-queue"
            element={
              <PrivateRoute>
                <CheckingQueue user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/quality-dashboard"
            element={
              <PrivateRoute>
                <QualityDashboard user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/planning-board/:projectId"
            element={
              <PrivateRoute>
                <PlanningBoard user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard-estimation"
            element={
              <PrivateRoute>
                <DashboardEstimation user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <RestrictedRoute allowedFor={[]}>
                <Projects user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <RestrictedRoute allowedFor={[]}>
                <ProjectDetail user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/project-planning"
            element={
              <RestrictedRoute allowedFor={[]}>
                <ProjectPlanningList user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/resource-allocation"
            element={
              <RestrictedRoute allowedFor={[]}>
                <ResourceAllocation user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/projects/:projectId/planning"
            element={
              <RestrictedRoute allowedFor={[]}>
                <ProjectPlanning user={user} />
              </RestrictedRoute>
            }
          />
          {/* Kanban route removed - replaced by Project Planning
          <Route
            path="/kanban/:projectId"
            element={
              <RestrictedRoute allowedFor={[]}>
                <Kanban user={user} />
              </RestrictedRoute>
            }
          />
          */}
          <Route
            path="/rfi-log"
            element={
              <PrivateRoute>
                <RFILog user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/transmittal-log"
            element={
              <PrivateRoute>
                <TransmittalLog user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/change-order-log"
            element={
              <PrivateRoute>
                <ChangeOrderLog user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/leave-management"
            element={
              <PrivateRoute>
                <LeaveManagement user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/leave-approvals"
            element={
              <PrivateRoute>
                <LeaveApprovals user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/hr/leave-allocations"
            element={
              <PrivateRoute>
                <HRLeaveAllocations user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/hr/leave-policy"
            element={
              <PrivateRoute>
                <LeavePolicyConfig user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/hr/leave-reports"
            element={
              <PrivateRoute>
                <LeaveReports user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/hr/holidays"
            element={
              <PrivateRoute>
                <HolidayCalendar user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/timesheets"
            element={
              <RestrictedRoute allowedFor={['Estimation-Team Member']}>
                <Timesheets user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/timesheet-calendar"
            element={
              <RestrictedRoute allowedFor={[]}>
                <TimesheetCalendar user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/timesheet-reports"
            element={
              <RestrictedRoute allowedFor={[]}>
                <TimesheetReports user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <RestrictedRoute allowedFor={[]}>
                <Invoices user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/invoices/:invoiceId"
            element={
              <RestrictedRoute allowedFor={[]}>
                <InvoiceDetail user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <PrivateRoute>
                <Clients user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/leads"
            element={
              <PrivateRoute>
                <Leads user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/lead-approvals"
            element={
              <RestrictedRoute allowedFor={["Manager", "Management", "Admin"]}>
                <LeadApprovals user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/interns"
            element={
              <RestrictedRoute allowedFor={["Admin", "Management"]} department="HR">
                <Interns user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/estimations"
            element={
              <PrivateRoute>
                <Estimations user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <RestrictedRoute allowedFor={[]}>
                <Reports user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <RestrictedRoute allowedFor={[]}>
                <Users user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/company-settings"
            element={
              <RestrictedRoute allowedFor={[]}>
                <CompanySettings user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <RestrictedRoute allowedFor={[]}>
                <Employees user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/teams"
            element={
              <RestrictedRoute allowedFor={[]}>
                <Teams user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/sales"
            element={
              <PrivateRoute>
                <Sales user={user} />
              </PrivateRoute>
            }
          />

          <Route
            path="/rfqs"
            element={
              <PrivateRoute>
                <RFQs user={user} />
              </PrivateRoute>
            }
          />

          <Route
            path="/vendors"
            element={
              <RestrictedRoute allowedFor={[]}>
                <Vendors user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/permissions"
            element={
              <RestrictedRoute allowedFor={[]}>
                <RBACViewer user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/rbac"
            element={
              <RestrictedRoute allowedFor={[]}>
                <RBACViewer user={user} />
              </RestrictedRoute>
            }
          />
          {/* Legacy route - keeping old PermissionMatrix for reference */}
          <Route
            path="/permissions-old"
            element={
              <RestrictedRoute allowedFor={[]}>
                <PermissionMatrix user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/error-settings"
            element={
              <RestrictedRoute allowedFor={[]}>
                <ErrorSettings user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/error-tracking"
            element={
              <PrivateRoute>
                <ErrorTracking user={user} />
              </PrivateRoute>
            }
          />
          <Route
            path="/webhook-settings"
            element={
              <RestrictedRoute allowedFor={[]}>
                <WebhookSettings user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/settings/wbs-master-list"
            element={
              <RestrictedRoute allowedFor={['/settings/wbs-master-list']}>
                <WBSMasterList user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/wbs-estimations"
            element={
              <RestrictedRoute allowedFor={['/wbs-estimations']}>
                <WBSEstimations user={user} />
              </RestrictedRoute>
            }
          />
          {/* Performance Management Module Routes */}
          <Route
            path="/performance/kra-settings"
            element={
              <RestrictedRoute allowedFor={[]}>
                <KRASettings user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/performance/employee-targets"
            element={
              <RestrictedRoute allowedFor={[]}>
                <EmployeeTargets user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/performance/summary"
            element={
              <RestrictedRoute allowedFor={[]}>
                <PerformanceSummary user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/performance/awards"
            element={
              <RestrictedRoute allowedFor={[]}>
                <PerformanceAwards user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/performance/my-performance"
            element={
              <RestrictedRoute allowedFor={[]}>
                <MyPerformance user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/performance/reports"
            element={
              <RestrictedRoute allowedFor={[]}>
                <PerformanceReports user={user} />
              </RestrictedRoute>
            }
          />
          {/* Client Feedback Module */}
          <Route
            path="/client-feedback"
            element={
              <RestrictedRoute allowedFor={[]}>
                <ClientFeedback user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/client-feedback/report"
            element={
              <RestrictedRoute allowedFor={[]}>
                <ClientFeedbackReport user={user} />
              </RestrictedRoute>
            }
          />
          {/* Expense Tracking Module Routes */}
          <Route
            path="/expense/settings"
            element={
              <RestrictedRoute allowedFor={[]}>
                <ExpenseSettings user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/expense/entry"
            element={
              <RestrictedRoute allowedFor={[]}>
                <ExpenseEntry user={user} />
              </RestrictedRoute>
            }
          />
          <Route
            path="/expense/employee-cost"
            element={
              <RestrictedRoute allowedFor={[]}>
                <ExpenseEmployeeCost user={user} />
              </RestrictedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster 
        position="top-right" 
        richColors 
        toastOptions={{
          style: { marginTop: '70px' }
        }}
      />
    </div>
  );
}

export default App;
export { toast };